import { DecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import { headlessDecorate } from "../headless-decorate";

import type { SerializedSurveyAnswersNode } from "./types";
import type { DOMExportOutput, ElementFormatType } from "lexical";

export class SurveyAnswersNode extends DecoratorBlockNode {
  __surveyId: string;
  __questionBold: boolean;
  __questionItalic: boolean;
  __questionUnderline: boolean;
  __questionColor: string;
  __answerBold: boolean;
  __answerItalic: boolean;
  __answerUnderline: boolean;
  __answerColor: string;

  constructor(
    surveyId = "",
    questionBold = true,
    questionItalic = false,
    questionUnderline = false,
    questionColor = "#000000",
    answerBold = false,
    answerItalic = false,
    answerUnderline = false,
    answerColor = "#000000",
    format?: ElementFormatType,
    key?: string,
  ) {
    super(format, key);
    this.__surveyId = surveyId;
    this.__questionBold = questionBold;
    this.__questionItalic = questionItalic;
    this.__questionUnderline = questionUnderline;
    this.__questionColor = questionColor;
    this.__answerBold = answerBold;
    this.__answerItalic = answerItalic;
    this.__answerUnderline = answerUnderline;
    this.__answerColor = answerColor;
  }

  static getType(): string {
    return "survey-answers";
  }

  static clone(node: SurveyAnswersNode): SurveyAnswersNode {
    return new this(
      node.__surveyId,
      node.__questionBold,
      node.__questionItalic,
      node.__questionUnderline,
      node.__questionColor,
      node.__answerBold,
      node.__answerItalic,
      node.__answerUnderline,
      node.__answerColor,
      node.__format,
      node.__key,
    );
  }

  static importJSON(serializedNode: SerializedSurveyAnswersNode): SurveyAnswersNode {
    return new this(
      serializedNode.surveyId,
      serializedNode.questionBold,
      serializedNode.questionItalic,
      serializedNode.questionUnderline ?? false,
      serializedNode.questionColor,
      serializedNode.answerBold,
      serializedNode.answerItalic,
      serializedNode.answerUnderline ?? false,
      serializedNode.answerColor,
      serializedNode.format,
    );
  }

  exportJSON(): SerializedSurveyAnswersNode {
    return {
      ...super.exportJSON(),
      type: "survey-answers",
      version: 2,
      surveyId: this.__surveyId,
      questionBold: this.__questionBold,
      questionItalic: this.__questionItalic,
      questionUnderline: this.__questionUnderline,
      questionColor: this.__questionColor,
      answerBold: this.__answerBold,
      answerItalic: this.__answerItalic,
      answerUnderline: this.__answerUnderline,
      answerColor: this.__answerColor,
    };
  }

  decorate(): React.JSX.Element {
    return headlessDecorate();
  }

  exportDOM(): DOMExportOutput {
    const dom = document.createElement("div");
    dom.setAttribute("data-node-type", "survey-answers");
    return { element: dom };
  }

  // Getters
  getSurveyId(): string {
    return this.getLatest().__surveyId;
  }

  getQuestionBold(): boolean {
    return this.getLatest().__questionBold;
  }

  getQuestionItalic(): boolean {
    return this.getLatest().__questionItalic;
  }
  getQuestionUnderline(): boolean {
    return this.getLatest().__questionUnderline;
  }

  getQuestionColor(): string {
    return this.getLatest().__questionColor;
  }

  getAnswerBold(): boolean {
    return this.getLatest().__answerBold;
  }

  getAnswerItalic(): boolean {
    return this.getLatest().__answerItalic;
  }
  getAnswerUnderline(): boolean {
    return this.getLatest().__answerUnderline;
  }

  getAnswerColor(): string {
    return this.getLatest().__answerColor;
  }

  // Setters
  setQuestionBold(v: boolean): void {
    this.getWritable().__questionBold = v;
  }

  setQuestionItalic(v: boolean): void {
    this.getWritable().__questionItalic = v;
  }
  setQuestionUnderline(v: boolean): void {
    this.getWritable().__questionUnderline = v;
  }

  setQuestionColor(v: string): void {
    this.getWritable().__questionColor = v;
  }

  setAnswerBold(v: boolean): void {
    this.getWritable().__answerBold = v;
  }

  setAnswerItalic(v: boolean): void {
    this.getWritable().__answerItalic = v;
  }
  setAnswerUnderline(v: boolean): void {
    this.getWritable().__answerUnderline = v;
  }

  setAnswerColor(v: string): void {
    this.getWritable().__answerColor = v;
  }

  setSurveyId(v: string): void {
    this.getWritable().__surveyId = v;
  }
}
